import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Button, FormGroup, Label, Input, Form } from 'reactstrap';
import PlanillaService from '../../axios_services/planilla.service';
import ArchivosService from '../../axios_services/archivos.service';
import Loader from '../Loader/Loader';
import { AppContext } from '../../application/provider';

/**
 * ModalEstadoPagoCargoPago
 * Modal para registrar Estado de Pago, Fecha de Pago y Comprobante de Pago
 * en el módulo RECIBO POR HONORARIOS - NEGOCIOS (CargoPago).
 * Disponible para perfiles: 8, 21, 26
 */
const ModalEstadoPagoCargoPago = ({ isOpen, onClose, onRefresh, item, periodo }) => {
    const [state] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);

    const [tipo_pagos] = useState(state.catalogos.tipos.filter(t => t.categoria_id == 32));
    const [fechaPago, setFechaPago] = useState('');
    const [estadoPagoId, setEstadoPagoId] = useState('');
    const [comprobanteFile, setComprobanteFile] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (isOpen && item) {
            setFechaPago(item.fecha_pago || '');
            setEstadoPagoId(item.estado_pago_id ? String(item.estado_pago_id) : '');
            setComprobanteFile(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [isOpen, item]);

    if (!item) return null;

    const handleRegistrar = async () => {
        setIsLoading(true);
        try {
            await PlanillaService.editarEstadoPago(
                state.user?.usuario_id,
                item.planilla_id,
                estadoPagoId,
                item.periodo_pago,
                null,
                fechaPago || null
            );

            if (comprobanteFile) {
                try {
                    await ArchivosService.guardarComprobantePago(
                        comprobanteFile,
                        item.usuario_id,
                        periodo,
                        state.user.usuario_id
                    );
                } catch (uploadError) {
                    const errorMsg = uploadError.response?.data?.message || '';
                    if (errorMsg.includes('failed to upload')) {
                        throw new Error('El archivo PDF es demasiado grande. Reduzca el tamaño (límite ~2MB) e intente nuevamente.');
                    }
                    throw uploadError;
                }
            }

            alert('Estado de pago, fecha y comprobante actualizados correctamente.');
            if (onRefresh) onRefresh();
            onClose();
        } catch (e) {
            console.error(e);
            alert(e.message || 'Error al actualizar el estado de pago. Inténtelo nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static" size="md" centered>
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>ESTADO DE PAGO - RH NEGOCIOS</h1>
                    <button className="close-btn" onClick={onClose}>X</button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body p-4">
                    {isLoading && <Loader />}

                    <Form onSubmit={(e) => { e.preventDefault(); handleRegistrar(); }} className="px-4 mt-2">

                        {/* ESTADO DE PAGO */}
                        <FormGroup className="mb-3">
                            <Label>Estado de Pago:</Label>
                            <Input
                                type="select"
                                className="form-control"
                                value={estadoPagoId}
                                onChange={(e) => setEstadoPagoId(e.target.value)}
                            >
                                <option value="">Seleccione un estado...</option>
                                {tipo_pagos.map(t => (
                                    <option key={t.tipo_id} value={String(t.tipo_id)}>{t.descripcion}</option>
                                ))}
                            </Input>
                        </FormGroup>

                        {/* FECHA DE PAGO - siempre visible */}
                        <FormGroup className="mb-3">
                            <Label>Fecha de Pago:</Label>
                            <Input
                                type="date"
                                className="form-control"
                                value={fechaPago}
                                onChange={(e) => setFechaPago(e.target.value)}
                            />
                        </FormGroup>

                        {/* COMPROBANTE DE PAGO */}
                        <FormGroup className="mb-4">
                            <Label>Subir / Actualizar Comprobante de Pago (PDF):</Label>
                            <div className="cap-upload-zone">
                                <span
                                    className="cap-upload-btn"
                                    onClick={() => fileRef.current?.click()}
                                >
                                    {comprobanteFile ? comprobanteFile.name : 'Seleccionar archivo (PDF)'}
                                </span>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: 'none' }}
                                    onChange={(e) => setComprobanteFile(e.target.files[0] || null)}
                                />
                            </div>
                            {comprobanteFile && (
                                <small>
                                    <span
                                        style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }}
                                        onClick={() => { setComprobanteFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                                    >
                                        ✕ Quitar selección
                                    </span>
                                </small>
                            )}
                            {item.comprobante_url && !comprobanteFile && (
                                <small className="text-success mt-1 d-block">
                                    ✓ Ya existe un comprobante subido.{' '}
                                    <span
                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => window.open(item.comprobante_url, '_blank')}
                                    >
                                        Ver documento
                                    </span>
                                </small>
                            )}
                        </FormGroup>

                        <div className="flex justify-end mt-4 pb-3">
                            <Button
                                className="btn-primary px-5 py-2"
                                onClick={handleRegistrar}
                                style={{ borderRadius: '8px' }}
                                disabled={isLoading}
                            >
                                GUARDAR CAMBIOS
                            </Button>
                        </div>
                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalEstadoPagoCargoPago;
